import React, { useState } from 'react';

import {
  Field,
  Select,
  SelectItem,
  Text,
  TextInput,
} from '@zapier/design-system';

import Link from 'app/common/components/Link';

import VersionModalWrapper from './VersionModalWrapper';

import type { SemVer } from 'app/developer-v3/types/version';

type DeprecationReasonOption = {
  label: string;
  value: string;
};

const DEPRECATION_REASONS: DeprecationReasonOption[] = [
  { label: 'API endpoint deprecated', value: 'api endpoint deprecated' },
  { label: 'Security vulnerability', value: 'security vulnerability' },
  { label: 'Critical bug', value: 'critical bug' },
  { label: 'Legal requirement', value: 'legal requirement' },
  { label: 'Other', value: 'other' },
];

const MINIMUM_DEPRECATION_DAYS = 21;
const MAXIMUM_DEPRECATION_DAYS = 364;

type Props = {
  date?: string;
  reason?: string;
  reasonComment?: string;
  isSaving: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onSetDate: (value: string) => void;
  onSetReason: (value: string) => void;
  onSetReasonComment: (value: string) => void;
  version: SemVer;
};

const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

const daysFromNow = (numDays: number) => {
  const d = new Date();
  d.setDate(d.getDate() + numDays);

  return toIsoDate(d);
};

const Deprecate = (props: Props) => {
  const [isOtherSelected, setIsOtherSelected] = useState(
    props.reason === 'other'
  );

  // Either have a reason, or a comment if "other" is selected.
  // The comment will be stored as the reason prefixed by 'other: {comment}'.
  const handleReasonChange = (key: React.Key) => {
    if (key === 'other') {
      props.onSetReason('other');
      setIsOtherSelected(true);
    } else {
      props.onSetReason(key as string);
      props.onSetReasonComment('');
      setIsOtherSelected(false);
    }
  };

  const handleCommentChange = (comment: string) => {
    props.onSetReasonComment(comment);
  };

  const isDateValid = date => {
    if (!date) {
      return false;
    }
    if (
      date < daysFromNow(MINIMUM_DEPRECATION_DAYS) ||
      date > daysFromNow(MAXIMUM_DEPRECATION_DAYS)
    ) {
      return false;
    }
    return true;
  };
  const isFormValid = () => {
    if (!isDateValid(props.date)) {
      return false;
    }
    if (
      props.reason === 'other' &&
      (!props.reasonComment || props.reasonComment.length > 50)
    ) {
      return false;
    }

    return true;
  };

  return (
    <VersionModalWrapper
      confirmButtonDisabled={!isFormValid()}
      confirmButtonLabel="Deprecate"
      heading={`Deprecate Version ${props.version}`}
      isSaving={props.isSaving}
      onClose={props.onClose}
      onConfirm={props.onConfirm}
    >
      <Text margin="10px 0px" tag="p">
        Deprecate integration versions to mark them as no longer usable in
        Zapier. Users with Zaps on these versions will receive email alerts and
        must transition to a newer version before the deprecation date, or their
        Zaps will pause.{' '}
        <Link
          href="https://docs.zapier.com/platform/publish/zap-templates#deprecating-versions-of-your-integration"
          target="_blank"
        >
          Update any Zap templates
        </Link>{' '}
        linked to deprecated versions. Use Deprecation only for versions with
        breaking changes. Otherwise, consider setting a version as "Legacy" to
        indicate that it is no longer recommended for use, but still functional.
        Only non-public versions can be deprecated, and an alternative version
        must be available for users.{' '}
        <Link
          href="https://docs.zapier.com/platform/manage/deprecate"
          target="_blank"
        >
          Learn more
        </Link>
        .
      </Text>
      <Text margin="10px 0px" tag="p">
        Select a deprecation date between 3 weeks and 1 year from today:
      </Text>
      <Text margin="20px 0px" tag="h3" type="Title">
        Deprecation Details
      </Text>
      <Select
        defaultSelectedKey={props.reason ?? null}
        label="Deprecation Reason"
        onChange={handleReasonChange}
        placeholder="Select a reason for deprecation"
        size="large"
      >
        {DEPRECATION_REASONS.map(reason => (
          <SelectItem key={reason.value} value={reason.value}>
            {reason.label}
          </SelectItem>
        ))}
      </Select>
      {isOtherSelected && (
        <Field
          label="Please specify the reason (50 characters max)"
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              maxLength={50}
              onChange={event => handleCommentChange(event.target.value)}
              placeholder="Enter the specific reason for deprecation"
              value={props.reasonComment || ''}
            />
          )}
        />
      )}
      <Field
        error={
          props.date && !isDateValid(props.date) ? 'Date is invalid' : null
        }
        label="Deprecation Date"
        renderInput={inputProps => (
          <input
            {...inputProps}
            aria-label="Deprecation Date"
            max={daysFromNow(MAXIMUM_DEPRECATION_DAYS)}
            min={daysFromNow(MINIMUM_DEPRECATION_DAYS)}
            onChange={event => props.onSetDate(event.target.value)}
            placeholder="dd/mm/yyyy" // Safari does not support type date
            type="date"
            value={props.date || ''}
          />
        )}
      />
    </VersionModalWrapper>
  );
};

export default Deprecate;
